"use client";

import { useWallet } from "@solana/wallet-adapter-react";
import { Wallet } from "lucide-react";

import { shortenAddress } from "@/lib/utils/address";
import { cn } from "@/lib/utils/cn";

type ConnectedWalletIndicatorProps = {
  address?: string | null;
  className?: string;
};

export const ConnectedWalletIndicator = ({
  address,
  className,
}: ConnectedWalletIndicatorProps) => {
  const { connected, connecting, publicKey, wallet } = useWallet();
  const publicKeyBase58 = publicKey?.toBase58() ?? address ?? null;

  if (wallet === null && publicKeyBase58 === null) return null;

  const walletName = wallet?.adapter.name ?? "Wallet";
  const walletIcon = wallet?.adapter.icon;
  const status = connecting
    ? "Connecting"
    : connected
      ? "Connected"
      : "Selected";

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2",
        className
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-input text-muted-foreground">
        {walletIcon !== undefined && walletIcon !== "" ? (
          <span
            aria-label={`${walletName} logo`}
            className="size-full bg-contain bg-center bg-no-repeat"
            role="img"
            style={{ backgroundImage: `url(${walletIcon})` }}
          />
        ) : (
          <Wallet className="size-4" />
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-xs font-semibold leading-none text-foreground">
          {walletName}
        </span>
        <span
          className="truncate font-mono text-xs leading-none text-muted-foreground"
          title={publicKeyBase58 ?? undefined}
        >
          {publicKeyBase58 !== null
            ? `${status}: ${shortenAddress(publicKeyBase58, 6)}`
            : status}
        </span>
      </span>
    </div>
  );
};
